// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import {
  ButtonGroup,
  type ButtonGroupProps,
  type Description,
  LgSpacing,
  Row,
  SecondaryPage,
  XsSpacing,
} from '@alto/design-system';
import React, { type ComponentProps } from 'react';
import { Platform, View } from 'react-native';

type SecondaryPageProps = ComponentProps<typeof SecondaryPage>;

type Props = Omit<SecondaryPageProps, 'buttons' | 'Footnote' | 'inlineButtons' | 'justifyButtons'> & {
  readonly buttons?: ButtonGroupProps['buttons'];
  readonly Footnote?: React.ReactElement<typeof Description>;
  readonly justifyButtons?: ButtonGroupProps['justifyButtons'];
  readonly inlineButtons?: ButtonGroupProps['inline'];
};

const IS_WEB = Platform.OS === 'web';
// flex:1 so SecondaryPage's flex-grow PageRoot still fills the screen, plus a 16px top margin
// between the apps/web sticky alto header and the SecondaryPage NavBar (per design).
const WEB_WRAPPER_STYLE = { flex: 1, marginTop: SPACING.STATIC.MD.value };

export const ProgynyWizardPage = ({
  buttons,
  Footnote,
  justifyButtons,
  inlineButtons = false,
  children,
  ...rest
}: Props) => {
  const hasButtons = !!buttons && buttons.length > 0;

  // On native, render SecondaryPage directly (no wrapper) with its `buttons` prop, so the
  // buttons stay anchored at the bottom via AnchoredButtonGroup — identical to the layout
  // before this wrapper existed. The View wrapper and inline-buttons logic below are only
  // applied on web, where the AnchoredButtonGroup gets hidden behind the apps/web page footer.
  if (!IS_WEB) {
    return (
      <SecondaryPage
        {...rest}
        buttons={hasButtons ? buttons : undefined}
        Footnote={Footnote}
        justifyButtons={justifyButtons}
        inlineButtons={inlineButtons}
      >
        {children}
      </SecondaryPage>
    );
  }

  return (
    <View style={WEB_WRAPPER_STYLE}>
      <SecondaryPage {...rest}>
        {children}
        {Footnote ? (
          <>
            <LgSpacing />
            <Row center>{Footnote}</Row>
            <XsSpacing />
          </>
        ) : null}
        {hasButtons ? (
          <>
            {!Footnote ? <LgSpacing /> : null}
            <ButtonGroup
              rowReversed
              buttons={buttons}
              inline={inlineButtons}
              justifyButtons={justifyButtons}
            />
          </>
        ) : null}
      </SecondaryPage>
    </View>
  );
};
